/**
 * Customer Details Block Style Generation
 *
 * Contains all styling logic for the Customer Details block
 *
 * @package RedfoxThankYou
 * @since 1.0.0
 */

import {
	BackgroundAttributes,
	getBackgroundCSS,
	getSpacingCSS,
	getTypographyCSS,
} from '@redfox-thank-you/control-groups';
import { createAttributeManager } from '@redfox-thank-you/utils';
import { applyFilters } from '@wordpress/hooks';

import type { CustomerDetailsAttributes } from './types';

/**
 * Generate CSS styles for the Customer Details block
 *
 * @param attributes - Block attributes containing style and settings data
 * @param blockClass - Unique CSS class for the block instance
 * @returns CSS string with all block styles
 */
export function generateCustomerDetailsCSS(
	attributes: CustomerDetailsAttributes,
	blockClass: string,
): string {
	const css: string[] = [];
	const blockSelector = `.${ blockClass }`;

	// Create attribute manager to safely access nested attributes
	const { getAttribute } = createAttributeManager(
		attributes as Record< string, unknown >,
		() => {},
	);

	// Extract style properties with defaults using getAttribute
	const blockTextStyle = getAttribute( 'style.block.text', {} );
	const blockBackgroundStyle = getAttribute(
		'style.block.background',
		{},
	) as BackgroundAttributes;
	const blockSpacingStyle = getAttribute( 'style.block.spacing', {} );
	const titleStyle = getAttribute( 'style.title', {} );
	const labelsStyle = getAttribute( 'style.labels', {} );

	// Extract settings properties with defaults using getAttribute
	const showTitle = getAttribute< boolean >( 'settings.title.show', true );
	const tableStyleEnabled = getAttribute< boolean >(
		'settings.layout.tableStyle',
		false,
	);
	const tableBorderColor = getAttribute< string >(
		'style.table.borderColor',
		'',
	);

	// Title styles
	if ( showTitle ) {
		const titleSelector = `${ blockSelector } .redfox-thank-you-customer-details__title`;

		// Use typography style function for title typography
		const titleTypographyCSS = getTypographyCSS(
			titleStyle,
			titleSelector,
		);
		if ( titleTypographyCSS ) {
			css.push( titleTypographyCSS );
		}
	}

	// Block text styles
	const blockTextTypographyCSS = getTypographyCSS(
		blockTextStyle,
		blockSelector,
	);
	if ( blockTextTypographyCSS ) {
		css.push( blockTextTypographyCSS );
	}

	// Label styles - use TypographyControl
	const labelSelector = `${ blockSelector } .redfox-thank-you-customer-details__label`;
	const labelTypographyCSS = getTypographyCSS( labelsStyle, labelSelector );
	if ( labelTypographyCSS ) {
		css.push( labelTypographyCSS );
	}

	// Background styles - responsive
	const backgroundCSS = getBackgroundCSS(
		blockBackgroundStyle,
		blockSelector,
	);
	if ( backgroundCSS ) {
		css.push( backgroundCSS );
	}

	// Spacing styles - Generate for desktop, tablet, and mobile
	const spacingStyles = getSpacingCSS( blockSpacingStyle, blockSelector );

	if ( spacingStyles ) {
		css.push( spacingStyles );
	}

	// Add overlay support styles - responsive aware
	const hasOverlay = [ 'desktop', 'tablet', 'mobile' ].some(
		( breakpoint ) => {
			const breakpointKey =
				breakpoint as keyof typeof blockBackgroundStyle;
			const breakpointValues = blockBackgroundStyle[ breakpointKey ];
			return breakpointValues?.backgroundOverlay?.enabled;
		},
	);

	if ( hasOverlay ) {
		css.push( `
			${ blockSelector } {
				position: relative;
			}
			${ blockSelector } .redfox-thank-you-customer-details__title,
			${ blockSelector } .redfox-thank-you-customer-details__content {
				position: relative;
				z-index: 2;
			}
		` );
	}

	// Table styles for border color
	if ( tableStyleEnabled && tableBorderColor ) {
		const tableSelector = `${ blockSelector } .redfox-thank-you-customer-details__content--table-style`;
		css.push(
			`${ tableSelector } { --table-border-color: ${ tableBorderColor } }`,
		);
	}

	/**
	 * Filter to extend block CSS generation.
	 * Pro plugins can use this to add additional CSS rules (e.g., border, box-shadow).
	 *
	 * @param {string} css - Current CSS string
	 * @param {Object} props - Props containing attributes and selector
	 * @param {Object} props.attributes - Block attributes
	 * @param {string} props.blockSelector - CSS selector for the block
	 * @param {string} props.blockName - Block name identifier
	 */
	const extendedCSS = applyFilters(
		'redfox_thank_you.block.css',
		css.join( '\n' ),
		{
			attributes,
			blockSelector,
			blockName: 'redfox-thank-you/customer-details',
		},
	) as string;

	return extendedCSS;
}
